import React, { useEffect, useRef, useState } from "react";
import { ChevronUp, ChevronDown, Phone, MessageCircle, Home, Sparkles, Coffee, User, MoreHorizontal } from "lucide-react";

export const LOCAL_ICONS = {
  house: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Travel%20and%20places/House.png",
  phone: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Telephone%20Receiver.png",
  chat: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Smilies/Speech%20Balloon.png",
  coffee: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Food/Hot%20Beverage.png",
  prayer: "/images/prayer_3d.png",
  heart: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Smilies/Red%20Heart.png",
  memo: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Memo.png",
  student: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/People/Person%20Raising%20Hand.png",
  warning: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Symbols/Warning.png",
  sparkles: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Activities/Sparkles.png",
  attendance: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Chart%20Increasing.png",
  teacher: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/People/Teacher.png",
};

export const DEFAULT_DEPT_COLORS = {
  영아부: "bg-pink-400",
  유아부: "bg-yellow-400",
  유치부: "bg-orange-400",
  유년부: "bg-sky-400",
  초등부: "bg-blue-500",
  중등부: "bg-purple-500",
  고등부: "bg-indigo-500",
  청년부: "bg-teal-600",
  장년부: "bg-gray-600",
  기타: "bg-gray-400",
};

export const FAITH_STATE_STYLES = {
  초신자: "bg-amber-400 text-white border-amber-400",
  기존신자: "bg-[#354F38] text-white border-[#354F38]",
  양육중: "bg-blue-500 text-white border-blue-500",
  시험듦: "bg-rose-500 text-white border-rose-500",
  장기결석: "bg-gray-400 text-white border-gray-400",
};

export function ExpandableText({ text, maxLines = 2, className = "" }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight, 10);
      const maxHeight = lineHeight * maxLines;
      setNeedsExpand(textRef.current.scrollHeight > maxHeight + 5);
    }
  }, [text, maxLines]);

  return (
    <div className="relative">
      <p
        ref={textRef}
        className={`text-xs text-gray-500 font-medium leading-relaxed mt-1 transition-all duration-300 ${className} ${!isExpanded ? `line-clamp-${maxLines}` : ""}`}
      >
        {text}
      </p>
      {needsExpand && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="text-[10px] font-black text-[#354F38] mt-1.5 flex items-center gap-0.5 hover:underline"
        >
          {isExpanded ? (
            <>
              <ChevronUp size={12} /> 접기
            </>
          ) : (
            <>
              <ChevronDown size={12} /> 더보기
            </>
          )}
        </button>
      )}
    </div>
  );
}

export function getLogIcon(type) {
  if (type.includes("전화")) return LOCAL_ICONS.phone;
  if (type.includes("문자") || type.includes("카톡")) return LOCAL_ICONS.chat;
  if (type.includes("가정")) return LOCAL_ICONS.house;
  if (type.includes("기도")) return LOCAL_ICONS.prayer;
  if (type.includes("교제") || type.includes("식사")) return LOCAL_ICONS.coffee;
  return LOCAL_ICONS.memo;
}

export function getSentimentIcon(sentiment) {
  switch (sentiment) {
    case "happy":
      return (
        <img
          src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Smilies/Grinning%20Face%20with%20Smiling%20Eyes.png"
          alt="happy"
          className="w-6 h-6 drop-shadow-sm"
        />
      );
    case "sad":
      return (
        <img
          src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Smilies/Crying%20Face.png"
          alt="sad"
          className="w-6 h-6 drop-shadow-sm"
        />
      );
    default:
      return (
        <img
          src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Smilies/Neutral%20Face.png"
          alt="neutral"
          className="w-6 h-6 drop-shadow-sm"
        />
      );
  }
}

export function getTypeIcon(type) {
  if (type.includes("전화")) return Phone;
  if (type.includes("문자") || type.includes("카톡")) return MessageCircle;
  if (type.includes("가정") || type.includes("심방")) return Home;
  if (type.includes("기도")) return Sparkles;
  if (type.includes("교제") || type.includes("식사")) return Coffee;
  if (type.includes("면담")) return User;
  return MoreHorizontal;
}

export function getTypeColor(type) {
  if (type.includes("면담") || type.includes("대화")) return { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200", hover: "hover:bg-emerald-50", active: "ring-emerald-200" };
  if (type.includes("전화")) return { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200", hover: "hover:bg-blue-50", active: "ring-blue-200" };
  if (type.includes("문자") || type.includes("카톡")) return { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200", hover: "hover:bg-amber-50", active: "ring-amber-200" };
  if (type.includes("기도")) return { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200", hover: "hover:bg-purple-50", active: "ring-purple-200" };
  if (type.includes("가정") || type.includes("심방")) return { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200", hover: "hover:bg-orange-50", active: "ring-orange-200" };
  return { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200", hover: "hover:bg-gray-50", active: "ring-gray-200" };
}
